import type { TextData } from "cmw-client";

export const ALLOWED_FORMATS = ["bold", "italic", "underline"] as const;

export type Format = (typeof ALLOWED_FORMATS)[number];

export type Op = { insert?: unknown; attributes?: Record<string, unknown> };

/** Quill's idea of an empty document is a single trailing newline, not an absent one. */
export const isEmptyDelta = (ops: Op[]): boolean =>
  ops.every((op) => typeof op.insert === "string" && op.insert.trim() === "");

/** Strips any attribute outside the toolbar's whitelist, so a pasted heading, colour or link
 *  cannot smuggle a format the toolbar can't undo and the renderer hasn't been designed for. */
export const sanitizeOps = (ops: Op[]): Op[] =>
  ops.map((op) => {
    if (!op.attributes) return op;
    const attributes = Object.fromEntries(
      Object.entries(op.attributes).filter(([key]) => (ALLOWED_FORMATS as readonly string[]).includes(key)),
    );
    return Object.keys(attributes).length > 0 ? { ...op, attributes } : { insert: op.insert };
  });

export const opsOf = (value: TextData | null | undefined): Op[] =>
  ((value?.data as { ops?: Op[] } | undefined)?.ops ?? []) as Op[];

/** The envelope in, identical `ops` out — or `null` for an empty document, never a bare newline. */
export const toTextData = (ops: Op[]): TextData | null => {
  const sanitized = sanitizeOps(ops);
  return isEmptyDelta(sanitized) ? null : { type: "quilljs", data: { ops: sanitized } };
};

/**
 * The document as plain text. `about_plaintext` was dropped from the schema, so every excerpt,
 * preview and word count derives its string from the delta instead. Non-string inserts (embeds)
 * contribute nothing — the format whitelist means there shouldn't be any.
 *
 * An unknown `type` yields "", so a document some future editor wrote is never half-rendered.
 */
export const deltaText = (value: TextData | null | undefined): string => {
  if (value && value.type !== "quilljs") return "";
  return opsOf(value)
    .map((op) => (typeof op.insert === "string" ? op.insert : ""))
    .join("")
    .trim();
};

/** Plain text as a one-paragraph document, so it renders through the same view as a delta. */
export const plainTextToTextData = (text: string): TextData => ({
  type: "quilljs",
  data: { ops: [{ insert: `${text}\n` }] },
});

/**
 * The About a visitor sees: the practitioner's own once it has any text, otherwise the bio a
 * legacy profile carried over from the old site. The one place "About is filled" is decided.
 */
export const displayAbout = (source: {
  about?: TextData | null;
  legacyAbout?: string | null;
}): TextData | undefined => {
  if (source.about && deltaText(source.about) !== "") return source.about;
  return source.legacyAbout ? plainTextToTextData(source.legacyAbout) : undefined;
};
